import type { StreakFlowVariant } from "@features/amplitude-flags/model";
import type { LessonCompletionResult } from "../model/result";
import type { LevelUp } from "@features/xp/model";

export type CompletionScreen =
    | "completing"
    | "lessonDone"
    | "dailyGoal"
    | "levelUp"
    | "pushPermission"
    | "streak"
    | "lessonFeedback"
    | "courseCompleted";

export type CompletionOutcome = "next" | "home";

export type CompletionFlowContext = {
    streakFlow: StreakFlowVariant;
    result: LessonCompletionResult | null;
    askPushPermission: boolean;
    levelUp: LevelUp | null;
};

type CompletionStep = {
    screen: CompletionScreen;
    when: (context: CompletionFlowContext) => boolean;
};

const isDailyGoalFlow = (context: CompletionFlowContext) => context.streakFlow === "daily_goal";
const isFirstCompletion = (context: CompletionFlowContext) => context.result?.isFirstCompletion === true;

export const LESSON_COMPLETION_FLOW: CompletionStep[] = [
    { screen: "completing", when: (context) => context.result === null && !isDailyGoalFlow(context) },
    { screen: "lessonDone", when: (context) => !isDailyGoalFlow(context) },
    { screen: "dailyGoal", when: (context) => isDailyGoalFlow(context) },
    { screen: "levelUp", when: (context) => context.levelUp !== null },
    { screen: "pushPermission", when: (context) => isFirstCompletion(context) && context.askPushPermission },
    {
        screen: "streak",
        when: (context) => !isDailyGoalFlow(context) && context.result?.isStreakExtended === true
    },
    { screen: "lessonFeedback", when: (context) => isFirstCompletion(context) },
    { screen: "courseCompleted", when: (context) => context.result?.isCourseCompleted === true }
];

export function nextCompletionScreen(
    current: CompletionScreen | null,
    context: CompletionFlowContext
): CompletionScreen | null {
    const start = current === null ? 0 : LESSON_COMPLETION_FLOW.findIndex((step) => step.screen === current) + 1;
    return LESSON_COMPLETION_FLOW.slice(start).find((step) => step.when(context))?.screen ?? null;
}

export type NextLesson = {
    courseId: number;
    topicId: number;
    lessonId: number;
};

export type CompletionExitContext = {
    outcome: CompletionOutcome;
    isFirstCompletion: boolean;
    isIntroLesson: boolean;
    isProductV3Flow: boolean;
    isProductV4Frame: boolean;
    isDailyGoalFlow: boolean;
    isDailyGoalComplete: boolean;
    nextLesson: NextLesson | null;
    courseId: number;
    courseType: "challenge" | "course";
    courseTags: string[];
    topicId: number;
    isTopicCompleted: boolean;
    source: string | null;
};

export type RouteExit = { type: "route"; url: string };
export type LessonExit = { type: "lesson"; lesson: NextLesson; source: string | null };
export type ParentFrameExit = { type: "parentFrame" };
export type CompletionExit = RouteExit | LessonExit | ParentFrameExit;

export const PRODUCT_V3_ONBOARDING_HOME = "/home?source=product_v3_onboarding&onboarding_step=1";

const route = (url: string): CompletionExit => ({ type: "route", url });

function courseRoute(context: CompletionExitContext): CompletionExit {
    const { courseId, courseType, courseTags, topicId, source } = context;

    if (courseType === "challenge" && !courseTags.includes("challenge")) {
        return route(`/challenge/${courseId}${source ? `?source=${source}` : ""}`);
    }
    return route(`/course/${courseId}${source ? `/topic/${topicId}?source=${source}` : ""}`);
}

function isChallenge(context: CompletionExitContext) {
    return context.courseType === "challenge" || context.courseTags.includes("challenge");
}

function exitAfterFeedback(context: CompletionExitContext): CompletionExit {
    if (context.isProductV4Frame) return { type: "parentFrame" };
    if (context.isProductV3Flow) return route(PRODUCT_V3_ONBOARDING_HOME);
    if (context.nextLesson && !context.isIntroLesson) {
        return { type: "lesson", lesson: context.nextLesson, source: context.source };
    }
    if (!isChallenge(context) && context.isTopicCompleted && context.source === "learning-plan") {
        return route("/learning-plan");
    }
    return courseRoute(context);
}

function exitAfterRevisit(context: CompletionExitContext): CompletionExit {
    if (context.isDailyGoalFlow && !context.isDailyGoalComplete && context.nextLesson) {
        return { type: "lesson", lesson: context.nextLesson, source: context.source };
    }
    if (context.isIntroLesson) {
        return route(context.isProductV3Flow ? PRODUCT_V3_ONBOARDING_HOME : "/learning-plan");
    }
    return courseRoute(context);
}

export function resolveCompletionExit(context: CompletionExitContext): CompletionExit {
    if (context.outcome === "home") return route("/home");
    return context.isFirstCompletion ? exitAfterFeedback(context) : exitAfterRevisit(context);
}

export function nextLessonByPosition(
    courseLessons: { topicId: number; lessons: { id: number }[] }[],
    courseId: number,
    lessonId: number
): NextLesson | null {
    const flat = courseLessons.flatMap((topic) =>
        topic.lessons.map((lesson) => ({ courseId, topicId: topic.topicId, lessonId: lesson.id }))
    );
    const index = flat.findIndex((lesson) => lesson.lessonId === lessonId);
    if (index === -1 || index >= flat.length - 1) return null;
    return flat[index + 1];
}
